import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import {
  LayoutDashboard,
  Package,
  ScanLine,
  Briefcase,
  BarChart3,
  Tags,
  ClipboardCheck,
  Settings,
  Users,
  FolderTree,
  Trash2,
  Upload,
  Database,
  ListTree,
  Menu,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '../ui/Button';

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
  roles?: string[];
}

// Main operations (all users)
const mainNav: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/scan', label: 'Scan Tool', icon: ScanLine },
];

// Inventory & Jobs (managers and up)
const inventoryNav: NavItem[] = [
  { to: '/inventory', label: 'Inventory', icon: Package },
  { to: '/jobs', label: 'Jobs', icon: Briefcase },
  { to: '/labels', label: 'Print Labels', icon: Tags },
  { to: '/audit', label: 'Audit', icon: ClipboardCheck, roles: ['admin', 'manager'] },
];

// Reports & Analytics (all users can view)
const reportsNav: NavItem[] = [
  { to: '/reports', label: 'Reports', icon: BarChart3 },
  { to: '/cost-analysis', label: 'Cost Analysis', icon: BarChart3 },
];

// Setup & Configuration (managers for setup, admin for users/system)
const setupNav: NavItem[] = [
  { to: '/admin/categories', label: 'Categories', icon: ListTree, roles: ['admin', 'manager'] },
  { to: '/admin/locations', label: 'Locations', icon: FolderTree, roles: ['admin', 'manager'] },
  { to: '/admin/discard-reasons', label: 'Discard Reasons', icon: Trash2, roles: ['admin', 'manager'] },
];

// Admin only (users, system settings, data management)
const adminNav: NavItem[] = [
  { to: '/admin/users', label: 'Users', icon: Users },
  { to: '/admin/import', label: 'Import/Export', icon: Upload },
  { to: '/admin/backup', label: 'Backup', icon: Database },
  { to: '/admin/settings', label: 'System Settings', icon: Settings },
];

export function Sidebar() {
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAdmin = user?.role === 'admin';
  const isManagerOrAbove = user?.role === 'admin' || user?.role === 'manager';

  const filterNav = (items: NavItem[]) => {
    return items.filter((item) => {
      if (!item.roles) return true;
      return item.roles.includes(user?.role || '');
    });
  };

  const NavSection = ({ title, items }: { title?: string; items: NavItem[] }) => {
    const filtered = filterNav(items);
    if (filtered.length === 0) return null;

    return (
      <>
        {title && (
          <div className="mt-6 mb-2 px-3 first:mt-0">
            <span className="text-xs font-semibold uppercase tracking-wide text-foreground-subtle">
              {title}
            </span>
          </div>
        )}
        <div className="space-y-1">
          {filtered.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                cn('nav-item', isActive && 'nav-item-active')
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </div>
      </>
    );
  };

  const NavContent = () => (
    <>
      {/* Logo */}
      <div className="h-16 flex items-center px-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <Package className="h-4 w-4 text-white" />
          </div>
          <span className="font-semibold text-lg">Tool Crib</span>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 overflow-y-auto p-4">
        <NavSection items={mainNav} />
        {isManagerOrAbove && <NavSection title="Inventory & Jobs" items={inventoryNav} />}
        <NavSection title="Reports" items={reportsNav} />
        {isManagerOrAbove && <NavSection title="Setup" items={setupNav} />}
        {isAdmin && <NavSection title="Administration" items={adminNav} />}
      </nav>

      {/* Settings at bottom */}
      <div className="p-4 border-t border-border">
        <NavLink
          to="/preferences"
          onClick={() => setMobileOpen(false)}
          className={({ isActive }) =>
            cn('nav-item', isActive && 'nav-item-active')
          }
        >
          <Settings className="h-4 w-4" />
          Preferences
        </NavLink>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile toggle */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          variant="secondary"
          size="icon"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </Button>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 w-64 bg-surface border-r border-border flex flex-col',
          'transition-transform duration-200 lg:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <NavContent />
      </aside>
    </>
  );
}
